import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { ExternalLink, Newspaper, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sourceColors = {
  'TechCrunch': 'bg-green-500/10 text-green-400 border-green-500/30',
  'Crunchbase News': 'bg-blue-500/10 text-blue-400 border-blue-500/30',
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const NewsFeed = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await axios.get(`${API}/public/news?limit=9`);
        setArticles(res.data);
      } catch {} finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  if (loading) {
    return (
      <section className="py-16 px-6" data-testid="news-feed-loading">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-400 mx-auto"></div>
        </div>
      </section>
    );
  }

  if (articles.length === 0) return null;

  return (
    <section className="py-16 px-6" data-testid="news-feed-section">
      <div className="container mx-auto max-w-6xl">
        <div className="flex items-center gap-3 mb-8">
          <Newspaper className="w-6 h-6 text-cyan-400" />
          <h2 className="text-2xl font-bold text-slate-100">Startup & VC News</h2>
          <span className="text-sm text-slate-500 ml-auto">Powered by TechCrunch & Crunchbase</span>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {articles.map((article, i) => (
            <a
              key={i}
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group"
              data-testid={`news-article-${i}`}
            >
              <Card className="bg-slate-800/40 border-slate-700 overflow-hidden h-full hover:border-cyan-500/40 transition-all duration-200 hover:-translate-y-0.5">
                {article.image && (
                  <div className="h-40 overflow-hidden">
                    <img
                      src={article.image}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${sourceColors[article.source] || 'bg-slate-700 text-slate-400 border-slate-600'}`}>
                      {article.source}
                    </span>
                    {article.published && (
                      <span className="text-xs text-slate-600 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {timeAgo(article.published)}
                      </span>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-slate-200 mb-2 line-clamp-2 group-hover:text-cyan-300 transition-colors">
                    {article.title}
                  </h3>
                  {article.summary && (
                    <p className="text-xs text-slate-500 line-clamp-3">{article.summary}</p>
                  )}
                  <div className="flex items-center gap-1 mt-3 text-xs text-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity">
                    Read more <ExternalLink className="w-3 h-3" />
                  </div>
                </div>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NewsFeed;
